import { Anchor, Button, Flex, Stack, Text, Title } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconInfoCircle } from '@tabler/icons-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { EORIGIN_DATE_FORMAT_WITH_TIME } from '@e-origin/shared';

import { Select, TextInput, UploadFile } from '@components';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';

const languages = [
  { label: 'Default', value: 'default' },
  { label: 'English', value: 'en' },
  { label: 'Français', value: 'fr' },
];

export const ImportCatalog = ({
  catalogTitle,
  forMultipleLanguages = false,
  onImport,
  draftAvailability,
  onPublish,
  type = 'xlsx',
  actionsDisabled,
  publishDisabled,
  downloadUrl,
  useFileURL = false,
}) => {
  const [importing, setImporting] = useState(false);

  const importCatalogSchema = z
    .object({
      lang: z.string().optional().nullable(),
      file: z.instanceof(File).optional().nullable(),
      fileURL: z.string().optional().nullable(),
      useFileURL: z.boolean(),
    })
    .superRefine(({ useFileURL, file, fileURL }, ctx) => {
      if (useFileURL && !fileURL) {
        ctx.addIssue({
          path: ['fileURL'],
          message: 'File URL is required',
          code: 'custom',
        });
      }
      if (!useFileURL && !file) {
        ctx.addIssue({
          path: ['file'],
          message: 'File is required',
          code: 'custom',
        });
      }
    });

  type ImportCatalogValues = z.infer<typeof importCatalogSchema>;

  const form = useFormWithNameInputProp<ImportCatalogValues>({
    initialValues: {
      lang: null,
      file: null,
      fileURL: null,
      useFileURL,
    },
    validate: zodResolver(importCatalogSchema),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (forMultipleLanguages) {
      form.setFieldValue('lang', languages[0].value);
    }
  }, [forMultipleLanguages]);

  const changesSelectedLanguage = (value) => {
    form.setFieldValue('lang', value ? languages.find((lang) => lang.value === value).value : null);
  };

  const handleImport = async (values: ImportCatalogValues) => {
    setImporting(true);

    await onImport(values);
    form.setFieldValue('file', null);
    form.setFieldValue('fileURL', null);

    setImporting(false);
  };

  const handlePublish = async () => {
    const createdAt = moment(draftAvailability(form.values.lang).createdAt).format(EORIGIN_DATE_FORMAT_WITH_TIME);

    modals.openConfirmModal({
      title: 'Publish collection',
      children: (
        <>
          <Text>Are you sure you want to publish this collection to production?</Text>
          <br />
          <Text>It was last updated {createdAt}</Text>
        </>
      ),
      labels: { confirm: 'Publish', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        onPublish({ lang: form.values.lang });
      },
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleImport)}>
      <Stack my={24} gap={24}>
        <Flex justify="space-between" align="center">
          <Title order={4} fw={600} display="flex">
            {catalogTitle}
          </Title>
          {draftAvailability(form.values.lang)?.available && (
            <Button type="button" variant="outline" size="sm" onClick={handlePublish} disabled={importing || actionsDisabled || publishDisabled}>
              Publish
            </Button>
          )}
        </Flex>

        {forMultipleLanguages && (
          <Select
            label="Select language"
            data={languages}
            {...form.getInputProps('lang')}
            onChange={changesSelectedLanguage}
            disabled={importing || actionsDisabled}
          />
        )}

        <Stack gap="xs">
          <Anchor href={downloadUrl} target="_blank" underline="always">
            File available for download here
          </Anchor>
          {useFileURL ? (
            <Stack gap="xs">
              <Flex gap="sm" align="center">
                <IconInfoCircle />
                <Text c="dimmed" size="sm" ta="center">
                  Please right click on the 'Download' button of the file from <br /> the above url, click on 'Copy link address'
                  <br /> and paste in the field below
                </Text>
              </Flex>
              <TextInput label="File URL" {...form.getInputProps('fileURL')} required />
            </Stack>
          ) : (
            <UploadFile {...form.getInputProps('file')} isMulti={false} type={type as any} p="xs" disabled={importing || actionsDisabled} />
          )}
        </Stack>

        <Button type="submit" loading={importing} disabled={!form.isValid() || actionsDisabled} variant="filled" size="lg">
          Import
        </Button>
      </Stack>
    </form>
  );
};
